/**
 * 1プレイの中の「助け」：コンティニューとおはし。
 *
 * - コンティニュー：ゲームオーバーのあと1回だけ。リワード広告を見て、こぼれた料理と上の3個を消して再開
 * - おはし：料理を1つつまんで消す。所持数を使う。持っていなければリワード広告で1プレイ1本もらえる
 *   （ショップから、または「こぼれそう！」のときに盤面のそばに出る「広告でおはし」から。2026-10-11）
 *
 * どちらかを使ったプレイは「アシストあり」として記録する。
 * アシストありの Lv12 も金メダルとして認めるが、白金メダル（腕前の証）には数えない。
 *
 * Web版（WebPlatform）では `enabled` が false になり、何も出ない。
 */

import type { Platform, PurchaseOutcome, RewardedPlacement } from '../platform/Platform';
import { PRODUCTS, type ProductId } from '../platform/products';
import type { Wallet } from './wallet';

export const ASSIST_TUNING = {
  /** コンティニューで、こぼれた料理に加えて上から消す個数。 */
  continueClearTop: 3,
  /** 1プレイでできるコンティニューの回数。 */
  continuesPerRun: 1,
  /** リワード広告でおはしをもらえる回数（1プレイあたり）。 */
  adItemsPerRun: 1,
  /** リワード広告でおはしをもらえる回数（1日あたり）。無制限だと買う理由がなくなる。 */
  adItemsPerDay: 3,
  /** 1プレイで使えるおはしの本数。「課金で無双」にしないため。 */
  itemsPerRun: 3,
  /**
   * 合間の広告（「もう一度あそぶ」を押したときだけ）。
   * 2026-10-11：回数ではなく時間で数える。1プレイが10分前後と長く、「1日の最初の3回は出さない・2回に1回」
   * だと1日3プレイの人にはまったく出なかった。前回の広告（合間・ごほうび）か起動から8分以上たっていれば出す。
   * 起動して最初の「もう一度」でも、遊んだのが8分未満なら出ない。
   */
  // 2026-10-13：起動からの時間ではなく「前回の広告から実際に遊んだ時間」で数える。
  // 起動からの時間だと、アプリを閉じては開いて遊ぶ人や、県を移る人で毎回0に戻り、一度も出なかった。
  // 遊んだ時間は端末に保存するので、閉じても続きから数える。
  interstitial: { every: 1, minIntervalMs: 0, minPlayMs: 8 * 60_000, freeGamesPerDay: 0 },
} as const;

export interface RunAssistState {
  usedItem: boolean;
  /** 「こぼれそう！」のときの「広告でおはし」をこのプレイで出したか（1プレイ1回まで）。 */
  rescueOffered?: boolean;
  /** このプレイで使ったおはしの本数。 */
  itemsUsed: number;
  continues: number;
  adItemsTaken: number;
}

type Listener = () => void;


/** これより長く使っていなければ「開き直し」とみなす（県の移動は数秒）。 */
const FRESH_LAUNCH_GAP_MS = 2 * 60_000;

export class AssistController {
  private run: RunAssistState = { usedItem: false, itemsUsed: 0, continues: 0, adItemsTaken: 0 };
  private readonly listeners = new Set<Listener>();
  private busy = false;
  /** true のあいだは合間の広告を出さない（配信モード）。ごほうび広告は本人が押したときだけなので止めない。 */
  blockInterstitial: () => boolean = () => false;

  /** 起動した時刻（診断の表示用）。 */
  private readonly sessionStartedAt: number;
  /**
   * アプリを開いた直後か（県の移動ではなく、閉じていたのを開き直した）。
   * 開いて最初の「はじめる」では合間の広告を出さない。
   */
  private readonly freshLaunch: boolean;
  /** このページで「はじめる」を押したか。 */
  private startedOnce = false;

  constructor(
    readonly platform: Platform,
    private readonly wallet: Wallet,
    /** 今の時刻（テストで差し替える）。 */
    private readonly clock: () => number = () => Date.now(),
  ) {
    const now = clock();
    this.sessionStartedAt = now;
    // 県の移動はページの読み直しだが、直前まで使っていたので「開き直し」ではない
    this.freshLaunch = now - wallet.lastActiveAt > FRESH_LAUNCH_GAP_MS;
    wallet.touchActive(now);
  }

  /**
   * 数秒ごとに呼ぶ（main.ts）。ゲーム中なら遊んだ時間を足す。アプリを使っている印も付ける。
   * @param playedMs 前回からゲームを遊んでいた時間（ゲーム中でなければ 0）
   */
  heartbeat(playedMs: number): void {
    this.wallet.touchActive(this.clock());
    if (this.enabled && playedMs > 0) this.wallet.addPlayTime(playedMs);
  }

  /**
   * 広告の診断（テスト用ビルドの設定画面に出す）。広告SDKの状態に、合間の広告を出す条件を足す。
   * テスト用でなければ null。
   */
  adDiagnostics(): string[] | null {
    const platformLines = this.platform.adDiagnostics?.() ?? null;
    if (!platformLines) return null;
    const now = this.clock();
    const w = this.wallet.interstitialSnapshot();
    const min = (ms: number) => `${(ms / 60_000).toFixed(1)}分`;
    const sinceAd = w.lastInterstitialAt > 0 ? min(now - w.lastInterstitialAt) : 'まだ無し';
    const need = ASSIST_TUNING.interstitial.minPlayMs ?? 0;
    const left = need - w.playMsSinceAd;
    return [
      `前回の広告から遊んだ時間：${min(w.playMsSinceAd)}`,
      `合間の広告まで：${left > 0 ? `あと${min(left)}遊ぶ` : '出す番（次の「はじめる」か「もう一度あそぶ」で出る）'}`,
      `前回の広告から（時計）：${sinceAd}`,
      `このページ：${this.freshLaunch ? 'アプリを開いた直後（最初の「はじめる」では出さない）' : '県の移動・続き'}／起動から${min(now - this.sessionStartedAt)}`,
      `広告なし（買い切り）：${this.wallet.adFree ? 'はい' : 'いいえ'}`,
      `配信者モード：${this.blockInterstitial() ? 'オン（合間の広告は出ない）' : 'オフ'}`,
      ...platformLines,
    ];
  }

  /** 広告・アイテム・課金を出すか（Web版は false）。 */
  get enabled(): boolean {
    return this.platform.monetized;
  }

  /** お金で買う商品を出すか（Android 版とWeb版は false）。 */
  get canPurchase(): boolean {
    return this.enabled && this.platform.canPurchase;
  }

  /**
   * 起動時：広告とストアの準備をして、買い切りを持っていれば黙って反映する
   * （機種変更・入れ直しのあと、「復元」を押さなくても広告が消えるように）。
   * ストアのログインは求めない。
   */
  async startup(): Promise<void> {
    if (!this.enabled) return;
    // iOS のトラッキング許可（ATT）は起動直後に聞く（2026-10-20 審査の差し戻しを受けて）。
    // 広告SDKの初期化（init）は、中で ATT の返事を待ってから行う。ここで先に呼ぶのは結果を計測に残すため
    void this.platform.askTrackingPermission().catch(() => undefined);
    try {
      await this.platform.init();
      if (!this.platform.canPurchase || this.wallet.adFree) return;
      const owned = await this.platform.ownedProducts();
      if (owned.includes('ad_free')) {
        this.wallet.setAdFree(true);
        this.emit();
      }
    } catch {
      /* 準備に失敗してもゲームは止めない */
    }
  }

  get items(): number {
    return this.wallet.items;
  }

  get adFree(): boolean {
    return this.wallet.adFree;
  }

  /** このプレイでコンティニューかおはしを使ったか。 */
  get assisted(): boolean {
    return this.run.usedItem || this.run.continues > 0;
  }

  get runState(): Readonly<RunAssistState> {
    return this.run;
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    this.listeners.forEach((l) => l());
  }

  /** 新しいプレイを始めるとき。 */
  resetRun(): void {
    this.run = { usedItem: false, itemsUsed: 0, continues: 0, adItemsTaken: 0 };
    this.emit();
  }

  /**
   * アプリ起動時：はじめての人への3本と、買い切り特典の毎日1本。
   * 受け取った本数を返す（UIで知らせる）。
   */
  claimStartupGifts(now: Date = new Date()): { welcome: number; daily: boolean } {
    if (!this.enabled) return { welcome: 0, daily: false };
    const welcome = this.wallet.grantWelcome();
    const daily = this.wallet.claimDaily(now);
    if (welcome || daily) this.emit();
    return { welcome, daily };
  }

  /** ごほうびのおはし（連続日数など）。Web版では何もしない。渡した本数を返す。 */
  giftItems(amount: number): number {
    if (!this.enabled || amount <= 0) return 0;
    this.wallet.add(amount);
    this.emit();
    return amount;
  }

  canContinue(): boolean {
    return this.enabled && this.run.continues < ASSIST_TUNING.continuesPerRun && !this.busy;
  }

  /** リワード広告を見てコンティニュー権を得る。見終わったら true。 */
  async requestContinue(): Promise<boolean> {
    if (!this.canContinue()) return false;
    const ok = await this.watch('continue');
    if (ok) {
      this.run.continues += 1;
      this.emit();
    }
    return ok;
  }

  /** このプレイでまだおはしを使えるか（1プレイ3本まで）。 */
  canUseItemThisRun(): boolean {
    return this.enabled && this.run.itemsUsed < ASSIST_TUNING.itemsPerRun;
  }

  /** おはしを1本使う（盤面の料理を消す直前に呼ぶ）。持っていない・上限なら false。 */
  consumeItem(): boolean {
    if (!this.canUseItemThisRun()) return false;
    if (!this.wallet.consume()) return false;
    this.run.usedItem = true;
    this.run.itemsUsed += 1;
    this.emit();
    return true;
  }

  canEarnItemByAd(): boolean {
    return (
      this.enabled &&
      this.run.adItemsTaken < ASSIST_TUNING.adItemsPerRun &&
      this.wallet.adItemsToday() < ASSIST_TUNING.adItemsPerDay &&
      !this.busy
    );
  }

  /** 広告でもらえない理由（ショップの表示用）。 */
  adItemLimitReason(): 'run' | 'day' | null {
    if (this.wallet.adItemsToday() >= ASSIST_TUNING.adItemsPerDay) return 'day';
    if (this.run.adItemsTaken >= ASSIST_TUNING.adItemsPerRun) return 'run';
    return null;
  }

  /**
   * 「こぼれそう！」のときに、盤面のそばに「広告でおはし」を出すか。
   * おはしを持っていない・このプレイでまだ使える・広告でもらえる・このプレイでまだ出していない、のときだけ。
   */
  canOfferRescue(): boolean {
    return (
      this.enabled &&
      this.wallet.items === 0 &&
      this.canUseItemThisRun() &&
      this.canEarnItemByAd() &&
      !this.run.rescueOffered
    );
  }

  /** 「広告でおはし」を出した（このプレイではもう出さない）。 */
  markRescueOffered(): void {
    this.run.rescueOffered = true;
  }

  /** 広告を見ておはしを1本もらう。どこから見たか（ショップ＝item／こぼれそう＝rescue）は計測に残す。 */
  async earnItemByAd(placement: 'item' | 'rescue' = 'item'): Promise<boolean> {
    if (!this.canEarnItemByAd()) return false;
    const ok = await this.watch(placement);
    if (ok) {
      this.run.adItemsTaken += 1;
      this.wallet.countAdItem();
      this.wallet.add(1);
      this.emit();
    }
    return ok;
  }

  async buy(productId: ProductId): Promise<PurchaseOutcome> {
    if (!this.canPurchase || this.busy) return 'failed';
    this.busy = true;
    try {
      let outcome: PurchaseOutcome;
      try {
        outcome = await this.platform.purchase(productId);
      } catch {
        outcome = 'failed';
      }
      if (outcome === 'purchased') this.applyPurchase(productId);
      return outcome;
    } finally {
      this.busy = false;
      this.emit();
    }
  }

  /** 買い切りの復元。restored＝戻った／none＝見つからない／failed＝ストアにつながらない。 */
  async restore(): Promise<'restored' | 'none' | 'failed'> {
    if (!this.canPurchase) return 'failed';
    let owned: ProductId[];
    try {
      owned = await this.platform.restorePurchases();
    } catch {
      return 'failed';
    }
    if (owned.includes('ad_free')) {
      this.wallet.setAdFree(true);
      this.emit();
      return 'restored';
    }
    return 'none';
  }

  /**
   * 県を移って「はじめる」を押したとき（2026-10-13）。
   * 県を巡る遊び方では「もう一度」を押さないので、ここでも合間の広告の番を見る。
   */
  async beforeStart(): Promise<void> {
    const first = !this.startedOnce;
    this.startedOnce = true;
    // アプリを開いて最初の「はじめる」では出さない（セッション開始時には絶対に出さない）
    if (first && this.freshLaunch) return;
    await this.betweenGames();
  }

  /**
   * ゲームオーバーのあと「もう一度」を押したとき。
   * 数回に1回、合間の広告を出す。買い切り済みなら出さない。
   * **セッション開始時には絶対に出さない**（ここは2回目以降のプレイの入口でしか呼ばない）。
   */
  async betweenGames(): Promise<void> {
    if (!this.enabled) return;
    if (this.blockInterstitial()) return;
    const before = this.wallet.interstitialSnapshot();
    if (!this.wallet.countGameForInterstitial(ASSIST_TUNING.interstitial, new Date(this.clock()), this.sessionStartedAt)) return;
    const shown = await this.platform.showInterstitial();
    // 出せなかったら「出した」記録を戻し、次の「もう一度」でまた試す（2026-10-12）
    if (shown === false) this.wallet.restoreInterstitial(before);
  }

  private applyPurchase(productId: ProductId): void {
    const product = PRODUCTS[productId];
    if (product.kind === 'non-consumable') {
      this.wallet.setAdFree(true);
      this.wallet.claimDaily();
    } else if (product.items) {
      this.wallet.add(product.items);
    }
  }

  private async watch(placement: RewardedPlacement): Promise<boolean> {
    this.busy = true;
    try {
      const ok = await this.platform.showRewarded(placement);
      // ごほうび広告を見た直後に合間の広告が続かないよう、これも「前回の広告」に数える
      if (ok) this.wallet.noteAdShown(new Date(this.clock()));
      return ok;
    } catch {
      return false;
    } finally {
      this.busy = false;
    }
  }
}

/**
 * コンティニュー（広告を見て続ける）を出すか。
 * 0点や、自己ベストの半分にも届かないプレイでは出さない（続けても得がなく、広告を見せるだけになる）。
 */
export function shouldOfferContinue(score: number, highScore: number): boolean {
  if (score <= 0) return false;
  if (highScore <= 0) return true;
  return score >= highScore * 0.5;
}
